import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { startTusUpload } from '../services/sourceUpload';
import { noteUploadActivity } from '../utils/axios';
import { useSelector } from 'react-redux';

const TUS_ENDPOINT = '/tus/files/';

/**
 * Global, persistent upload manager. Lives at the app root so transfers
 * survive page navigation. Each task carries its own AbortController so the
 * UI can cancel an upload at any time.
 *
 * State shape per task:
 *   id: string
 *   fileName: string
 *   size: number             total bytes
 *   loaded: number           bytes uploaded so far
 *   speed: number            bytes/sec (smoothed)
 *   etaSeconds: number|null
 *   status: 'queued'|'uploading'|'paused'|'source_unavailable'|'done'|'error'|'cancelled'
 *   error?: string
 *   uploadUrl?: string       saved tus URL when source_unavailable, used to resume
 *   companyId?: number       saved so reselect can re-issue with the same context
 *   flags?: object           saved per-source-product flags (watermarks/dcp)
 *   onComplete?: () => void  optional callback fired after a successful upload
 *
 *  `source_unavailable` is a recoverable, NON-terminal state — fires when
 *  the local File handle is no longer readable (SMB share dropped, USB
 *  ejected, file moved, browser tab restored). The user is shown a
 *  "Re-select file to resume" action that points the upload at a fresh
 *  File while reusing the already-uploaded server bytes.
 */
const UploadManagerContext = createContext(null);

let nextId = 1;
const ALPHA = 0.3; // EWMA smoothing for speed
const AUTO_DISMISS_MS = 5000;
// Terminal = no further state transitions possible. source_unavailable is
// NOT terminal: reselectAndResume can flip it back to 'uploading'.
const TERMINAL = new Set(['done', 'error', 'cancelled']);

export function UploadManagerProvider({ children }) {
  const activeCompanyId = useSelector((s) => s.company?.activeCompanyId);
  const [tasks, setTasks] = useState([]);
  // Per-task: tus.Upload instance (so we can abort), speed state
  const uploadsRef = useRef(new Map());
  const speedStateRef = useRef(new Map());

  const dismissTimersRef = useRef(new Map());

  const updateTask = useCallback((id, patch) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    // If the task just hit a terminal state, schedule auto-dismissal so the
    // overlay disappears 5 seconds after the transfer finishes (unless the
    // user dismisses or cancels it manually first).
    if (patch && patch.status && TERMINAL.has(patch.status)) {
      const existing = dismissTimersRef.current.get(id);
      if (existing) clearTimeout(existing);
      const t = setTimeout(() => {
        setTasks((prev) => prev.filter((task) => task.id !== id));
        dismissTimersRef.current.delete(id);
      }, AUTO_DISMISS_MS);
      dismissTimersRef.current.set(id, t);
    }
  }, []);

  const enqueue = useCallback(
    (file, opts = {}) => {
      const id = String(nextId++);
      speedStateRef.current.set(id, { lastTs: Date.now(), lastLoaded: 0, smoothed: 0 });

      const effectiveCompanyId = opts.companyId || activeCompanyId;
      const effectiveFlags = opts.flags;
      const task = {
        id,
        fileName: file.name,
        size: file.size,
        loaded: 0,
        speed: 0,
        etaSeconds: null,
        status: 'uploading',
        error: null,
        resumable: true,
        // Saved so reselectAndResume can keep using the same tenant +
        // product visibility flags when the user picks a fresh File.
        companyId: effectiveCompanyId,
        flags: effectiveFlags,
        // Saved on the onComplete callback so a later reselect-resume
        // still triggers the original "refresh files list" hook.
        _onComplete: opts.onComplete,
      };
      setTasks((prev) => [...prev, task]);

      const upload = startTusUpload(file, {
        endpoint: TUS_ENDPOINT,
        companyId: effectiveCompanyId,
        flags: effectiveFlags,
        onProgress: (loaded /*, total*/) => {
          // Suppress callbacks for already-cancelled uploads
          if (!uploadsRef.current.has(id)) return;
          // v10.21.37: long uploads (50 GB / 3 h) used to trip the
          // 60-min idle-logout because the user was just watching
          // the bar, not clicking. Ping the auth subsystem on every
          // chunk so the in-flight transfer keeps the session alive.
          noteUploadActivity();
          const ss = speedStateRef.current.get(id);
          if (!ss) return;
          const now = Date.now();
          const dt = (now - ss.lastTs) / 1000;
          if (dt > 0.25) {
            const inst = (loaded - ss.lastLoaded) / dt;
            ss.smoothed = ss.smoothed === 0 ? inst : ALPHA * inst + (1 - ALPHA) * ss.smoothed;
            ss.lastTs = now;
            ss.lastLoaded = loaded;
          }
          const remaining = file.size - loaded;
          const eta = ss.smoothed > 0 ? remaining / ss.smoothed : null;
          updateTask(id, { loaded, speed: ss.smoothed, etaSeconds: eta });
        },
        onSuccess: () => {
          if (!uploadsRef.current.has(id)) return;
          updateTask(id, { status: 'done', loaded: file.size, etaSeconds: 0 });
          opts.onComplete?.();
          uploadsRef.current.delete(id);
          speedStateRef.current.delete(id);
        },
        onError: (msg) => {
          // If the task was already cancelled, swallow the late retry errors
          // that fire after upload.abort(true) tears down the in-flight chunk.
          if (!uploadsRef.current.has(id)) return;
          const isAbort = (typeof msg === 'string' && /aborted/i.test(msg));
          if (isAbort) {
            updateTask(id, { status: 'cancelled' });
          } else {
            updateTask(id, { status: 'error', error: msg });
          }
          uploadsRef.current.delete(id);
          speedStateRef.current.delete(id);
        },
        onSourceUnavailable: ({ url, message }) => {
          // Local file became unreadable mid-transfer (SMB drop is the
          // classic case). Stash the tus URL so reselectAndResume() can
          // wire a fresh File against the same partial spool. Status is
          // NON-terminal so the row stays put until the user re-selects
          // (or cancels).
          if (!uploadsRef.current.has(id)) return;
          updateTask(id, {
            status: 'source_unavailable',
            error: message,
            uploadUrl: url,
          });
          // Drop the upload instance — its retry loop is dead. We'll
          // build a new tus.Upload when the user re-picks the file.
          uploadsRef.current.delete(id);
          speedStateRef.current.delete(id);
        },
      });
      uploadsRef.current.set(id, upload);

      return { id, promise: Promise.resolve() };
    },
    [updateTask, activeCompanyId],
  );

  /**
   * Resume a `source_unavailable` upload by re-binding it to a freshly-
   * picked File from the user. The server still has the partially-
   * uploaded bytes; tus-js-client skips POST-creation when given
   * `uploadUrl` and PATCHes from the server's known offset.
   *
   * Refuses the re-bind if the new File doesn't match the original by
   * name AND size — anything else is almost certainly a different file
   * and would corrupt the server-side spool.
   */
  const reselectAndResume = useCallback((id, newFile) => {
    let task;
    setTasks((prev) => {
      task = prev.find((t) => t.id === id);
      return prev;
    });
    if (!task) return { ok: false, error: 'Upload not found' };
    if (task.status !== 'source_unavailable') {
      return { ok: false, error: `Cannot resume from status ${task.status}` };
    }
    if (newFile.name !== task.fileName || newFile.size !== task.size) {
      return {
        ok: false,
        error: `File doesn't match the original (expected "${task.fileName}", ${task.size} bytes; got "${newFile.name}", ${newFile.size} bytes). Pick the same file to resume, or cancel and start a new upload.`,
      };
    }
    speedStateRef.current.set(id, { lastTs: Date.now(), lastLoaded: task.loaded || 0, smoothed: 0 });
    updateTask(id, { status: 'uploading', error: null });

    const upload = startTusUpload(newFile, {
      endpoint: TUS_ENDPOINT,
      companyId: task.companyId,
      flags: task.flags,
      uploadUrl: task.uploadUrl,
      onProgress: (loaded) => {
        if (!uploadsRef.current.has(id)) return;
        const ss = speedStateRef.current.get(id);
        if (!ss) return;
        const now = Date.now();
        const dt = (now - ss.lastTs) / 1000;
        if (dt > 0.25) {
          const inst = (loaded - ss.lastLoaded) / dt;
          ss.smoothed = ss.smoothed === 0 ? inst : ALPHA * inst + (1 - ALPHA) * ss.smoothed;
          ss.lastTs = now;
          ss.lastLoaded = loaded;
        }
        const remaining = newFile.size - loaded;
        const eta = ss.smoothed > 0 ? remaining / ss.smoothed : null;
        updateTask(id, { loaded, speed: ss.smoothed, etaSeconds: eta });
      },
      onSuccess: () => {
        if (!uploadsRef.current.has(id)) return;
        updateTask(id, { status: 'done', loaded: newFile.size, etaSeconds: 0 });
        try { task._onComplete?.(); } catch (_) { /* swallow */ }
        uploadsRef.current.delete(id);
        speedStateRef.current.delete(id);
      },
      onError: (msg) => {
        if (!uploadsRef.current.has(id)) return;
        const isAbort = (typeof msg === 'string' && /aborted/i.test(msg));
        updateTask(id, isAbort ? { status: 'cancelled' } : { status: 'error', error: msg });
        uploadsRef.current.delete(id);
        speedStateRef.current.delete(id);
      },
      onSourceUnavailable: ({ url, message }) => {
        if (!uploadsRef.current.has(id)) return;
        updateTask(id, { status: 'source_unavailable', error: message, uploadUrl: url });
        uploadsRef.current.delete(id);
        speedStateRef.current.delete(id);
      },
    });
    uploadsRef.current.set(id, upload);
    return { ok: true };
  }, [updateTask]);

  const cancel = useCallback(async (id) => {
    const upload = uploadsRef.current.get(id);
    if (!upload) return;
    // Mark cancelled immediately so the UI flips state right away. We also
    // remove from uploadsRef so any late onProgress / onError callbacks
    // (fired by tus-js-client retry logic after the in-flight chunk is
    // torn down) are swallowed instead of resurrecting the task.
    uploadsRef.current.delete(id);
    speedStateRef.current.delete(id);
    updateTask(id, { status: 'cancelled' });
    try {
      // shouldTerminate=true sends DELETE to the server, which removes
      // the partial file and the .info sidecar via tusd's post-terminate
      // hook. Awaiting so we can swallow any final error.
      await upload.abort(true);
    } catch (_) { /* ignore */ }
  }, [updateTask]);

  const pause = useCallback((id) => {
    const upload = uploadsRef.current.get(id);
    if (upload) {
      try { upload.abort(false); } catch (_) { /* ignore */ }
      updateTask(id, { status: 'paused' });
    }
  }, [updateTask]);

  const resume = useCallback((id) => {
    const upload = uploadsRef.current.get(id);
    if (upload) {
      try { upload.start(); updateTask(id, { status: 'uploading' }); } catch (_) { /* ignore */ }
    }
  }, [updateTask]);

  const dismiss = useCallback((id) => {
    const timer = dismissTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      dismissTimersRef.current.delete(id);
    }
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearFinished = useCallback(() => {
    // Cancel any pending auto-dismiss timers for tasks we're about to remove
    setTasks((prev) => {
      const keep = prev.filter((t) => t.status === 'uploading' || t.status === 'queued');
      const removed = prev.filter((t) => !keep.includes(t));
      removed.forEach((t) => {
        const timer = dismissTimersRef.current.get(t.id);
        if (timer) {
          clearTimeout(timer);
          dismissTimersRef.current.delete(t.id);
        }
      });
      return keep;
    });
  }, []);

  const value = useMemo(
    () => ({ tasks, enqueue, cancel, pause, resume, dismiss, clearFinished, reselectAndResume }),
    [tasks, enqueue, cancel, pause, resume, dismiss, clearFinished, reselectAndResume],
  );

  return <UploadManagerContext.Provider value={value}>{children}</UploadManagerContext.Provider>;
}

export function useUploadManager() {
  const ctx = useContext(UploadManagerContext);
  if (!ctx) throw new Error('useUploadManager must be used within UploadManagerProvider');
  return ctx;
}
